import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Select, FileUpload, RadioGroup, FormInput, LoadingSpinner } from '@/components';
import { ArrowRight } from 'lucide-react';
import { EDUCATION_OPTIONS, EDUCATION_LEVEL_MAP, EDUCATION_LEVELS } from './education-info.constants';
import { UserType } from '@/constants';
import { useApi } from '@/api/useApi';
import { useMutationProvider, useQueryProvider } from '@/hooks';
import '@/assets/styles/components/education-info.styles.scss';
import { DocumentTypes, useDocumentUpload } from '@/hooks/use-document-upload';
import { EducationInfoProps, FormFields, FileKeys } from './education-info.types';
import { QUERY_KEYS } from '@/constants/QueryKeys.ts';
import { useSizes } from '@/utility/responsive.ts';

const transformToSelectOptions = (data: any[] = []) => {
	if (!data) return [];
	return data.map(item => ({
		value: item.id.toString(),
		label: item.value
	}));
};

const validateInputText = (text: string) => {
	return text.replace(/[^a-zA-ZğüşıöçĞÜŞİÖÇ\s]/g, '');
};

const capitalizeWords = (text: string) => {
	return text
		.split(' ')
		.map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
		.join(' ');
};

const validateStudentFormFields = (formData: any, documentTypes: any[]): boolean => {
	if (!formData.university || !formData.department) {
		return false;
	}

	if (!documentTypes || documentTypes.length === 0) {
		return false;
	}

	return documentTypes.every(docType => {
		return !!formData[`document_${docType.id}`]?.file;
	});
};

interface SelectFieldProps {
	fieldKey: string;
	label: string;
	formData: any;
	onFormDataChange: (data: any) => void;
	options: any[];
	placeholder: string;
	customDependencies?: Record<string, any>;
	disabled?: boolean;
	parentFieldKey?: string;
}

const SelectField = ({ fieldKey, label, formData, onFormDataChange, options, placeholder, customDependencies = {}, disabled, parentFieldKey }: SelectFieldProps) => {
	const isCustomInput = formData[`${fieldKey}Custom`];
	const inputRef = useRef<HTMLInputElement>(null);

	const [localValue, setLocalValue] = useState(formData[fieldKey] || '');

	const isDepartmentField = fieldKey.toLowerCase().includes('department');
	const universityFieldKey = isDepartmentField ? parentFieldKey : null;
	const isUniversityCustom = universityFieldKey ? formData[`${universityFieldKey}Custom`] : false;

	const handleInputBlur = () => {
		if (localValue !== formData[fieldKey]) {
			onFormDataChange({
				...formData,
				[fieldKey]: localValue,
				[`${fieldKey}Custom`]: true
			});
		}
	};

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const validText = validateInputText(e.target.value);
		const capitalizedText = capitalizeWords(validText);
		setLocalValue(capitalizedText);
	};

	useEffect(() => {
		if ((isDepartmentField && isUniversityCustom) || isCustomInput) {
			setLocalValue(formData[fieldKey] || '');
		}
	}, [isCustomInput, isDepartmentField, isUniversityCustom, formData, fieldKey]);

	const handleSelectChange = (e: { target: { value: string } }) => {
		const newValue = e.target.value;
		const updatedData = { ...formData };

		if (newValue === 'Listede yok') {
			updatedData[fieldKey] = '';
			updatedData[`${fieldKey}Display`] = 'Listede yok';
			updatedData[`${fieldKey}Custom`] = true;

			if (customDependencies) {
				Object.keys(customDependencies).forEach(key => {
					updatedData[key] = customDependencies[key];
				});
			}
		} else {
			updatedData[fieldKey] = newValue;
			updatedData[`${fieldKey}Display`] = newValue;
			updatedData[`${fieldKey}Custom`] = false;

			if (customDependencies) {
				Object.keys(customDependencies).forEach(key => {
					updatedData[key] = '';
					updatedData[`${key}Custom`] = false;
				});
			}
		}

		onFormDataChange(updatedData);
	};

	if ((isDepartmentField && isUniversityCustom) || isCustomInput) {
		return (
			<>
				{isUniversityCustom ||
					(isCustomInput && !isDepartmentField && (
						<div className="form-section">
							<h3>{label}</h3>
							<Select
								options={[...options, { value: 'Listede yok', label: 'Listede yok' }]}
								placeholder={placeholder}
								value={formData[`${fieldKey}Display`] || formData[fieldKey]}
								onChange={handleSelectChange}
								disabled={disabled || (parentFieldKey && !formData[parentFieldKey])}
							/>
						</div>
					))}
				<div className="form-section">
					<h3>{label}</h3>
					<FormInput
						ref={inputRef}
						placeholder={`${label} adını yazın`}
						value={localValue}
						onChange={handleInputChange}
						onBlur={handleInputBlur}
					/>
				</div>
			</>
		);
	}

	return (
		<div className="form-section">
			<h3>{label}</h3>
			<Select
				options={[...options, { value: 'Listede yok', label: 'Listede yok' }]}
				placeholder={placeholder}
				value={formData[`${fieldKey}Display`] || formData[fieldKey]}
				onChange={handleSelectChange}
				disabled={disabled || (parentFieldKey && !formData[parentFieldKey])}
			/>
		</div>
	);
};

export const EducationInfo = ({ formData, onFormDataChange, onNext }: EducationInfoProps) => {
	const { isMobile } = useSizes();

	const [isUploading, setIsLoading] = useState<boolean>(false);
	const { uploadDocument } = useDocumentUpload();
	const { DocumentTypeControllerApi, ExpertApplicationControllerApi, LookUpControllerApiApplicant } = useApi();

	const uploadLabels = useMemo(() => {
		return {
			bachelorDegree: isMobile
				? 'Mezuniyet belgenizi telefonunuzdan belge seçerek yukarıdaki alana yükleyin.'
				: 'Mezuniyet belgenizi bu alana sürükleyip bırakın ya da bilgisayarınızdan bir dosya yükleyin.',
			master: 'Yüksek Lisans öğrenci belgenizi yükleyin.',
			masterDegree: 'Yüksek Lisans mezuniyet belgenizi yükleyin.',
			studentCertificate: 'Lisans mezuniyet belgenizi yükleyin.',
			default: 'Mezuniyet belgenizi yükleyin.'
		};
	}, [isMobile]);

	const EDUCATION_CONFIG = useMemo(() => {
		return {
			bachelor: {
				fields: ['bachelorUniversity', 'bachelorDepartment'],
				requiredFiles: ['bachelorDegree'],
				uploadText: isMobile
					? 'Lisans mezuniyet belgenizi telefonunuzdan belge seçerek yukarıdaki alana yükleyin.'
					: 'Lisans mezuniyet belgenizi yukarıdaki alana sürükleyip bırakın ya da bilgisayarınızdan bir dosya yükleyin.'
			},
			master: {
				fields: ['bachelorUniversity', 'bachelorDepartment', 'masterUniversity', 'masterDepartment'],
				requiredFiles: ['bachelorDegree', 'masterDegree'],
				uploadText: isMobile
					? 'Lisans ve yüksek lisans mezuniyet belgenizi telefonunuzdan belge seçerek yukarıdaki alana yükleyin.'
					: 'Lisans ve yüksek lisans mezuniyet belgenizi yukarıdaki alana sürükleyip bırakın ya da bilgisayarınızdan bir dosya yükleyin.'
			},
			ongoing_master: {
				fields: ['bachelorUniversity', 'bachelorDepartment', 'ongoingUniversity', 'ongoingDepartment'],
				requiredFiles: ['bachelorDegree', 'studentCertificate'],
				uploadText: isMobile
					? 'Lisans mezuniyet belgenizi ve yüksek lisans öğrenci belgenizi telefonunuzdan belge seçerek yukarıdaki alana yükleyin.'
					: 'Lisans mezuniyet belgenizi ve yüksek lisans öğrenci belgenizi yukarıdaki alana sürükleyip bırakın ya da bilgisayarınızdan bir dosya yükleyin.'
			}
		};
	}, [isMobile]);

	const validateGraduateForm = (formData: any): boolean => {
		if (!formData.type || !EDUCATION_CONFIG[formData.type]) {
			return false;
		}

		const config = EDUCATION_CONFIG[formData.type];

		const hasRequiredFields = config.fields.every(field => {
			return !!formData[field];
		});

		if (!hasRequiredFields) {
			return false;
		}

		return config.requiredFiles.every(fileKey => {
			return !!formData[fileKey]?.file;
		});
	};

	const isStudent = formData.userType === UserType.UNIVERSITY_STUDENT;

	const { data: documentTypes } = useQueryProvider(
		[QUERY_KEYS.getDocumentTypes, formData.userType!],
		() => DocumentTypeControllerApi.getDocumentTypes('DOCUMENT', EDUCATION_LEVELS[formData?.userType as string]),
		{
			select: ({ data }) => data.data,
			refetchOnWindowFocus: false,
			enabled: !!formData?.userType
		}
	);

	const { data: universitiesData } = useQueryProvider([QUERY_KEYS.getAllUniversities], () => LookUpControllerApiApplicant.getAllUniversities(), {
		select: ({ data }) => data.data,
		refetchOnWindowFocus: false
	});

	const { data: graduateProgramsData } = useQueryProvider(
		[QUERY_KEYS.getAllGraduatePrograms, formData?.masterUniversity, formData.ongoingUniversity],
		() => LookUpControllerApiApplicant.getAllGraduatePrograms(formData?.masterUniversity || formData.ongoingUniversity),
		{
			select: ({ data }) => data.data,
			refetchOnWindowFocus: false,
			enabled: (!!formData.masterUniversity || !!formData.ongoingUniversity) && !formData.masterUniversityCustom && !formData.ongoingUniversityCustom
		}
	);

	const { data: undergraduateProgramsData } = useQueryProvider(
		['getAllUndergraduatePrograms', formData?.bachelorUniversity || formData?.university],
		() => LookUpControllerApiApplicant.getAllUndergraduatePrograms(formData?.bachelorUniversity || formData?.university),
		{
			select: ({ data }) => data.data,
			refetchOnWindowFocus: false,
			enabled: (!!formData.university || !!formData.bachelorUniversity) && !formData.universityCustom && !formData.bachelorUniversityCustom
		}
	);

	const universities = useMemo(() => transformToSelectOptions(universitiesData), [universitiesData]);

	const undergraduatePrograms = useMemo(() => transformToSelectOptions(undergraduateProgramsData), [undergraduateProgramsData]);

	const graduatePrograms = useMemo(() => transformToSelectOptions(graduateProgramsData), [graduateProgramsData]);

	const createEducationInformation = useMutationProvider((body: any) => ExpertApplicationControllerApi.createEducationInformation(body), {
		onSuccess: () => onNext()
	});

	const handleFileUpload = useCallback(
		(fileKey: FileKeys | string) => async (file: File | null) => {
			setIsLoading(true);
			if (file && documentTypes) {
				let docType;

				if (isStudent) {
					docType = documentTypes?.[0];
				} else {
					docType = documentTypes?.find(dt => dt.graduationLevelId === EDUCATION_LEVELS[fileKey]);
				}

				if (docType) {
					const uploadResult = await uploadDocument(file, DocumentTypes.DOCUMENT, docType.id!);

					if (uploadResult) {
						onFormDataChange({
							...formData,
							[fileKey]: {
								file,
								name: file.name,
								documentTypeId: docType.id,
								documentId: uploadResult.id,
								documentLink: uploadResult.documentLink
							}
						});
					}
				}
			}
			setIsLoading(false);
		},
		[documentTypes, formData, isStudent, onFormDataChange, uploadDocument]
	);

	const handleFileRemove = useCallback(
		(fileKey: FileKeys | string) => () => {
			onFormDataChange({
				...formData,
				[fileKey]: undefined
			});
		},
		[formData, onFormDataChange]
	);
	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		const getUniversityInfo = (universityField: string, isCustom: boolean) => {
			if (isCustom) {
				return {
					id: 0,
					name: universityField || ''
				};
			}

			return {
				id: parseInt(universityField || '0'),
				name: universities.find(uni => uni.value === universityField)?.label || ''
			};
		};

		const getDepartmentInfo = (departmentField: string, isCustom: boolean, programList) => {
			if (isCustom) {
				return {
					id: 0,
					name: departmentField || ''
				};
			}

			return {
				id: parseInt(departmentField || '0'),
				name: programList.find(prog => prog.value === departmentField)?.label || ''
			};
		};

		if (isStudent) {
			const university = getUniversityInfo(formData.university, formData.universityCustom);
			const department = getDepartmentInfo(formData.department, formData.departmentCustom, undergraduatePrograms);

			const studentData = {
				graduationLevel: 'ONGOING_UNDERGRADUATE',
				universityId: university.id,
				universityName: university.name,
				departmentId: department.id,
				departmentName: department.name,
				graduateUniversityId: 0,
				graduateUniversityName: '',
				graduateDepartmentId: 0,
				graduateDepartmentName: ''
			};

			await createEducationInformation.mutateAsync(studentData);
		} else {
			const graduationLevel = EDUCATION_LEVEL_MAP[formData.type];

			const bachelorUniversity = getUniversityInfo(formData.bachelorUniversity, formData.bachelorUniversityCustom === true);

			const bachelorDepartment = getDepartmentInfo(formData.bachelorDepartment, formData.bachelorDepartmentCustom === true, undergraduatePrograms);

			let informationData = {
				graduationLevel,
				universityId: bachelorUniversity.id,
				universityName: bachelorUniversity.name,
				departmentId: bachelorDepartment.id,
				departmentName: bachelorDepartment.name,
				graduateUniversityId: 0,
				graduateUniversityName: '',
				graduateDepartmentId: 0,
				graduateDepartmentName: ''
			};

			if (graduationLevel === 'ONGOING_POSTGRADUATE' || graduationLevel === 'POSTGRADUATE_DEGREE') {
				const universityField = graduationLevel === 'ONGOING_POSTGRADUATE' ? formData.ongoingUniversity : formData.masterUniversity;

				const departmentField = graduationLevel === 'ONGOING_POSTGRADUATE' ? formData.ongoingDepartment : formData.masterDepartment;

				const universityCustom = graduationLevel === 'ONGOING_POSTGRADUATE' ? formData.ongoingUniversityCustom === true : formData.masterUniversityCustom === true;

				const departmentCustom = graduationLevel === 'ONGOING_POSTGRADUATE' ? formData.ongoingDepartmentCustom === true : formData.masterDepartmentCustom === true;

				const graduateUniversity = getUniversityInfo(universityField, universityCustom);
				const graduateDepartment = getDepartmentInfo(departmentField, departmentCustom, graduatePrograms);

				informationData = {
					...informationData,
					graduateUniversityId: graduateUniversity.id,
					graduateUniversityName: graduateUniversity.name,
					graduateDepartmentId: graduateDepartment.id,
					graduateDepartmentName: graduateDepartment.name
				};
			}

			await createEducationInformation.mutateAsync(informationData);
		}
	};

	const renderGuideFields = () => {
		if (!formData.type) return null;

		const config = EDUCATION_CONFIG[formData.type];
		if (!config) return null;

		const fieldComponents: Record<FormFields, () => JSX.Element> = {
			bachelorUniversity: () => (
				<SelectField
					key="bachelorUniversity"
					fieldKey="bachelorUniversity"
					label="Lisans / Üniversite"
					formData={formData}
					onFormDataChange={onFormDataChange}
					options={universities}
					placeholder="Üniversite seçin"
					customDependencies={{
						bachelorDepartment: '',
						bachelorDepartmentCustom: false
					}}
				/>
			),
			bachelorDepartment: () => (
				<SelectField
					key="bachelorDepartment"
					fieldKey="bachelorDepartment"
					label="Lisans / Bölüm"
					formData={formData}
					onFormDataChange={onFormDataChange}
					options={undergraduatePrograms}
					placeholder="Bölüm seçin"
					parentFieldKey="bachelorUniversity"
				/>
			),
			masterUniversity: () => (
				<SelectField
					key="masterUniversity"
					fieldKey="masterUniversity"
					label="Yüksek Lisans / Üniversite"
					formData={formData}
					onFormDataChange={onFormDataChange}
					options={universities}
					placeholder="Üniversite seçin"
					customDependencies={{
						masterDepartment: '',
						masterDepartmentCustom: null
					}}
				/>
			),
			masterDepartment: () => (
				<SelectField
					key="masterDepartment"
					fieldKey="masterDepartment"
					label="Yüksek Lisans / Bölüm"
					formData={formData}
					onFormDataChange={onFormDataChange}
					options={graduatePrograms}
					placeholder="Bölüm seçin"
					parentFieldKey="masterUniversity"
				/>
			),
			ongoingUniversity: () => (
				<SelectField
					key="ongoingUniversity"
					fieldKey="ongoingUniversity"
					label="Yüksek Lisans / Üniversite"
					formData={formData}
					onFormDataChange={onFormDataChange}
					options={universities}
					placeholder="Üniversite seçin"
					customDependencies={{
						ongoingDepartment: '',
						ongoingDepartmentCustom: false
					}}
				/>
			),
			ongoingDepartment: () => (
				<SelectField
					key="ongoingDepartment"
					fieldKey="ongoingDepartment"
					label="Yüksek Lisans / Bölüm"
					formData={formData}
					onFormDataChange={onFormDataChange}
					options={graduatePrograms}
					placeholder="Bölüm seçin"
					parentFieldKey="ongoingUniversity"
				/>
			)
		};

		return config.fields.map(field => {
			const Component = fieldComponents[field];
			return Component && <Component key={field} />;
		});
	};

	const renderDocumentUploads = () => {
		if (!formData.type || !EDUCATION_CONFIG[formData.type]) return null;

		const config = EDUCATION_CONFIG[formData.type];
		const { requiredFiles, uploadText } = config;

		return (
			<div className="form-section">
				<h3>{formData?.type === 'bachelor' ? 'Mezuniyet Belgesi' : 'Belge Yükle'}</h3>
				<div className={`upload-grid ${requiredFiles.length === 1 ? 'single-upload' : ''}`}>
					{requiredFiles.map(fileKey => (
						<FileUpload
							key={fileKey}
							label={formData?.type !== 'bachelor' && fileKey === 'bachelorDegree' ? uploadLabels.studentCertificate : uploadLabels[fileKey]}
							buttonText={formData?.type === 'bachelor' ? 'Belge Yükle' : ''}
							onChange={handleFileUpload(fileKey)}
							currentFile={formData[fileKey]}
							onRemove={handleFileRemove(fileKey)}
						/>
					))}
				</div>
				<p className="upload-info">{uploadText}</p>
			</div>
		);
	};

	const isFormValid = isStudent ? validateStudentFormFields(formData, documentTypes || []) : validateGraduateForm(formData);

	const handleEducationTypeChange = (value: string) => {
		const resetForm = {
			...formData,
			type: value,
			bachelorDegree: undefined,
			masterDegree: undefined,
			master: undefined,
			studentCertificate: undefined,

			bachelorUniversity: undefined,
			bachelorUniversityCustom: undefined,
			bachelorUniversityDisplay: undefined,

			bachelorDepartment: undefined,
			bachelorDepartmentCustom: undefined,
			bachelorDepartmentDisplay: undefined,

			masterUniversity: undefined,
			masterUniversityCustom: undefined,
			masterUniversityDisplay: undefined,

			masterDepartment: undefined,
			masterDepartmentCustom: undefined,
			masterDepartmentDisplay: undefined,

			ongoingUniversity: undefined,
			ongoingUniversityCustom: undefined,
			ongoingUniversityDisplay: undefined,

			ongoingDepartment: undefined,
			ongoingDepartmentCustom: undefined,
			ongoingDepartmentDisplay: undefined,

			...Object.keys(formData)
				.filter(key => key.startsWith('document_'))
				.reduce((acc, key) => ({ ...acc, [key]: undefined }), {})
		};

		onFormDataChange(resetForm);
	};

	if (isStudent) {
		return (
			<div className="education-form-container">
				<form
					onSubmit={handleSubmit}
					className="education-form">
					<div className="form-header">
						<h1>Eğitim Bilgileri</h1>
						<p>Devam ettiğiniz üniversite ve bölüm bilgilerini eksiksiz ve doğru bir şekilde doldurun.</p>
					</div>

					<SelectField
						fieldKey="university"
						label="Üniversite"
						formData={formData}
						onFormDataChange={onFormDataChange}
						options={universities}
						placeholder="Üniversite seçin"
						customDependencies={{
							department: '',
							departmentCustom: false
						}}
					/>

					<SelectField
						fieldKey="department"
						label="Bölüm"
						formData={formData}
						onFormDataChange={onFormDataChange}
						options={undergraduatePrograms}
						placeholder="Bölüm seçin"
						parentFieldKey="university"
					/>

					<div className="form-section">
						<h3>Belge Yükle</h3>
						<div className="upload-grid">
							{documentTypes?.map(docType => (
								<FileUpload
									key={docType.id}
									label={docType.name}
									onChange={handleFileUpload(`document_${docType.id}`)}
									currentFile={formData[`document_${docType.id}`]}
									onRemove={handleFileRemove(`document_${docType.id}`)}
								/>
							))}
						</div>
						<p className="upload-info">Gerekli belgeleri yukarıdaki alana sürükleyip bırakın ya da bilgisayarınızdan yükleyin.</p>
					</div>

					<button
						type="submit"
						className={`submit-button ${!isFormValid ? 'disabled' : ''}`}
						disabled={!isFormValid}>
						<span>Devam Et</span>
						<ArrowRight size={20} />
					</button>
				</form>
				{isUploading && <LoadingSpinner loadingText="Belge yükleniyor..." />}
			</div>
		);
	}

	return (
		<div className="education-form-container">
			<form
				onSubmit={handleSubmit}
				className="education-form">
				<div className="form-header">
					<h1>Eğitim Bilgileri</h1>
					<p>Mezun olduğunuz üniversite ve bölüm bilgilerini eksiksiz ve doğru bir şekilde doldurun.</p>
				</div>

				<div className="form-section">
					<h3>Üniversite Bilgisi</h3>
					<RadioGroup
						options={EDUCATION_OPTIONS}
						value={formData.type}
						onChange={handleEducationTypeChange}
						name="education-type"
					/>
				</div>

				{renderGuideFields()}
				{formData.type && (
					<>
						{renderDocumentUploads()}
						<button
							type="submit"
							className={`submit-button ${!isFormValid ? 'disabled' : ''}`}
							disabled={!isFormValid}>
							<span>Devam Et</span>
							<ArrowRight size={20} />
						</button>
					</>
				)}
			</form>
			{isUploading && <LoadingSpinner loadingText={formData?.type === 'bachelor' ? 'Mezuniyet Belgesi yükleniyor...' : 'Belge yükleniyor...'} />}
		</div>
	);
};

export default EducationInfo;
